import { twMerge } from 'tailwind-merge';

import type {
  DodgeballMatch,
  DodgeballMatchPenalty,
} from '@/types/sports/dodgeball';
import { formatTime } from '@/utils/time';

const formatSetTime = (
  timings: { setTime: number; resetTime: number; timeoutTime: number },
  matchState: string,
  setState: string,
) => {
  switch (matchState) {
    case 'break':
    case 'timeout':
      return formatTime(timings.timeoutTime);
    case 'running':
      if (setState === 'finished' && timings.resetTime > 0) {
        return formatTime(timings.resetTime);
      } else {
        return formatTime(timings.setTime);
      }
    default:
      return formatTime(timings.setTime);
  }
};

export const TimingInfo = ({
  matchConfig,
  matchState,
  timings,
  homePenalties,
  awayPenalties,
}: {
  matchConfig: DodgeballMatch['config'];
  matchState: DodgeballMatch['state'];
  timings: {
    matchTime: number;
    resetTime: number;
    setTime: number;
    timeoutTime: number;
  };
  homePenalties: DodgeballMatchPenalty[];
  awayPenalties: DodgeballMatchPenalty[];
}) => {
  const { final_set, set_state, match_state } = matchState;

  const formattedSetTime = formatSetTime(timings, match_state, set_state);
  const formattedMatchTime =
    match_state === 'break' ? 'Half Time' : formatTime(timings.matchTime);

  const showSetClock =
    (matchConfig.show_set_clock && !final_set) || match_state === 'break';

  return (
    <div className="flex w-full flex-col space-y-[5vh] px-[5vw] text-center">
      <div
        className={`${
          showSetClock && !final_set ? 'text-[45vh]' : 'text-[60vh]'
        } text-center leading-none`}
      >
        {showSetClock
          ? formattedSetTime
          : final_set
            ? formattedSetTime
            : formattedMatchTime}
      </div>
      <div className="flex">
        <div
          className={`flex flex-1 ${
            showSetClock && !final_set
              ? 'flex-col justify-end space-y-[1vh]'
              : 'flex-row justify-start space-x-[5vw]'
          } items-stretch text-slate-500`}
        >
          {homePenalties
            ? homePenalties.map((penalty: DodgeballMatchPenalty) =>
                penalty.time && penalty.time > 0 ? (
                  <div
                    key={`home-penalties-${penalty.participant?.id || 'team'}`}
                    className="text-left text-[12vh]"
                  >
                    {formatTime(penalty.time)}
                  </div>
                ) : null,
              )
            : null}
        </div>
        {final_set ? null : (
          <div
            className={twMerge(
              'max-w-[60vw] pb-[5vh] text-center text-slate-700',
              match_state === 'break'
                ? 'text-[15vh] uppercase'
                : 'text-[30vh] leading-[15vh]',
            )}
          >
            {showSetClock ? formattedMatchTime : ''}
          </div>
        )}
        <div
          className={`flex flex-1 ${
            showSetClock && !final_set
              ? 'flex-col space-y-[1vh]'
              : 'flex-row space-x-[5vw]'
          } items-stretch justify-end text-slate-500`}
        >
          {awayPenalties
            ? awayPenalties.map((penalty) =>
                penalty.time && penalty.time > 0 ? (
                  <div
                    key={`away-penalties-${penalty.participant?.id || 'team'}`}
                    className="text-right text-[12vh]"
                  >
                    {formatTime(penalty.time)}
                  </div>
                ) : null,
              )
            : null}
        </div>
      </div>
    </div>
  );
};
